import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Plane, Eye, EyeOff, Mail, Lock, User, ArrowRight, Shield, Globe, Award } from 'lucide-react';
import PasswordStrengthMeter from '@/components/common/PasswordStrengthMeter';

export default function LoginPage() {
  const [mode, setMode] = useState('login');
  const { t, i18n } = useTranslation();
  const { language, toggleLanguage } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const API = process.env.REACT_APP_BACKEND_URL;

  const _homeFor = (u) => {
    const role = u?.role;
    if (role === 'partner_admin' || role === 'partner_instructor') return '/partner-portal';
    if (role === 'student') return '/my-portal';
    return '/';
  };

  useEffect(() => {
    if (user) {
      navigate(_homeFor(user), { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      let nextUser;
      if (mode === 'login') {
        nextUser = await login(email, password);
      } else {
        await axios.post(`${API}/api/auth/register`, { email, password, name, role: 'student' });
        nextUser = await login(email, password);
      }
      // Save language preference to user profile
      try { await axios.put(`${API}/api/auth/preferences`, { language_preference: i18n.language }, { withCredentials: true }); } catch {}
      navigate(_homeFor(nextUser), { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Shield, label: 'Multi-Authority Compliance', desc: 'EASA · FAA · GACA · SACAA' },
    { icon: Globe, label: 'Global Standards', desc: 'International best practices' },
    { icon: Award, label: 'Certified Training', desc: 'Professional pilot programs' },
  ];

  return (
    <div className="min-h-screen flex bg-background" data-testid="login-page">
      {/* Language Toggle - Top Right */}
      <div className="fixed top-4 right-4 z-50">
        <button
          onClick={toggleLanguage}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary/50 border border-border text-sm font-medium hover:bg-secondary/80 transition-colors"
          data-testid="login-lang-toggle"
        >
          <Globe className="w-4 h-4" />
          {language === 'ar' ? 'العربية' : 'English'}
        </button>
      </div>
      {/* Left Panel - Hero Image & Branding */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        {/* Background Image */}
        <img
          src="https://images.unsplash.com/photo-1761813409441-5c157440ab98?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhaXJwbGFuZSUyMGNvY2twaXQlMjBuaWdodCUyMHZpZXd8ZW58MHx8fHwxNzcwNDE5NTk2fDA&ixlib=rb-4.1.0&q=85"
          alt="Modern cockpit at night"
          className="object-cover w-full h-full"
        />
        
        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent" />
        
        {/* Content */}
        <div className="absolute inset-0 flex flex-col justify-start gap-8 p-12">
          {/* Top - Logo */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 flex items-center justify-center border border-cyan-500/30 backdrop-blur-sm">
              <Plane className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>SAGER AVIATION</h1>
              <p className="text-xs text-cyan-400 font-medium tracking-widest uppercase">Intelligence</p>
            </div>
          </div>
          
          {/* Bottom - Features */}
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl font-bold text-white leading-tight mb-3" style={{ fontFamily: 'Outfit' }}>
                Aviation Training<br />
                <span className="text-gradient">Management System</span>
              </h2>
              <p className="text-muted-foreground text-base max-w-md leading-relaxed">
                Streamline your flight school operations with comprehensive tools for training management, compliance tracking, and student progress monitoring.
              </p>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              {features.map((feature) => (
                <div 
                  key={feature.label}
                  className="glass-card rounded-xl p-4 hover:border-cyan-500/20 transition-all duration-300"
                >
                  <feature.icon className="w-5 h-5 text-cyan-400 mb-3" />
                  <p className="text-sm font-semibold text-white mb-1">{feature.label}</p>
                  <p className="text-xs text-muted-foreground">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-start justify-center px-6 pt-6 pb-4 lg:px-12 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="flex items-center gap-3 mb-6 lg:hidden">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500/20 to-cyan-500/5 flex items-center justify-center border border-cyan-500/30">
              <Plane className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-lg font-bold" style={{ fontFamily: 'Outfit' }}>SAGER AVIATION</h1>
              <p className="text-[10px] text-cyan-400 font-medium tracking-widest uppercase">Intelligence</p>
            </div>
          </div>

          {/* Header */}
          <div className="mb-5">
            <h1 className="heading-xl text-foreground mb-2">
              {mode === 'login' ? 'Welcome back' : 'Create account'}
            </h1>
            <p className="text-muted-foreground">
              {mode === 'login' 
                ? 'Sign in to access your flight operations dashboard' 
                : 'Get started with SAGER AVIATION'}
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <div 
              data-testid="auth-error" 
              className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-start gap-3 animate-scale-in"
            >
              <Shield className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Authentication Error</p>
                <p className="text-red-400/80 text-xs mt-0.5">{error}</p>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            {mode === 'register' && (
              <div className="space-y-1.5">
                <label className="label-text">{t('form.fullName')}</label>
                <div className="relative group">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-cyan-400 transition-colors" />
                  <input
                    data-testid="register-name-input"
                    type="text"
                    placeholder="Enter your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 bg-secondary/50 border border-border rounded-xl text-sm focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20 outline-none transition-all"
                    required
                  />
                </div>
              </div>
            )}
            
            <div className="space-y-1.5">
              <label className="label-text">Email Address</label>
              <div className="relative group">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-cyan-400 transition-colors" />
                <input
                  data-testid="login-email-input"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-secondary/50 border border-border rounded-xl text-sm focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20 outline-none transition-all"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-1.5">
              <label className="label-text">Password</label>
              <div className="relative group">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-cyan-400 transition-colors" />
                <input
                  data-testid="login-password-input"
                  type={showPwd ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-11 pr-12 py-3 bg-secondary/50 border border-border rounded-xl text-sm focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20 outline-none transition-all"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {mode === 'register' && (
                <PasswordStrengthMeter password={password} userInputs={[email, name].filter(Boolean)} />
              )}
            </div>

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full py-3.5 mt-2 btn-primary rounded-xl font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 group"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {mode === 'login' ? 'Sign In' : 'Create Account'}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </button>
          </form>

          {/* Forgot password link (only on login mode) */}
          {mode === 'login' && (
            <p className="mt-3 text-center text-xs">
              <button
                type="button"
                data-testid="forgot-password-link"
                onClick={() => navigate('/forgot-password')}
                className="text-muted-foreground hover:text-cyan-300 transition-colors"
              >
                Forgot your password?
              </button>
            </p>
          )}

          {/* Toggle Mode */}
          <p className="mt-5 text-center text-sm text-muted-foreground">
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
              className="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors"
            >
              {mode === 'login' ? 'Create one' : 'Sign in'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
